/**
 * Modal host for the pharma questionnaire page.
 *
 * Renders document merge, submission summary, Astera submission, and
 * Prediction Summary modals based on the active Redux modal id.
 *
 * @module pharmaQuestionaire/components/QuestionnaireModals
 */
import { useDispatch } from "react-redux";
import { closeModal } from "risa-oasis-ui_v2";
import AsterasubmissionModal from "../../../components/modals/asteraSubmission/asteraSubmission";
import DocumentsModalWithMerge from "../../../components/modals/documentsModalWithMerge/documentsModalWithMerge";
import PredictionSummaryModal from "../../../components/modals/predictionSummaryModal/predictionSummaryModal";
import SubmissionSummary from "../../../components/modals/submissionSummary/submissionSummary";
import {
  NycbsPharmaOrderModel,
  PharmaStpFileModel,
} from "../../../data-model/nycbsPharmaOrder";
import { ModalId } from "../../../enums/modalId";
import { NycbsPharmaOrderKeys } from "../../../enums/nycbsPharmaOrder";
import { setOpenedModalId } from "../../../redux/slice/modalSliceNew";
import { AppDispatch } from "../../../redux/store/store";

/**
 * Props for questionnaire page modals.
 */
interface QuestionnaireModalsProps {
  /** Closes the documents / merge modal and clears Redux modal id. */
  onCloseDocumentsModal: () => void;
  /** Identifier used by the documents merge modal. */
  identifier: string;
  /** Identifier used to fetch Prediction Summary / MN judge assessment. */
  predictionSummaryIdentifier: string;
  documents: File[];
  fileNames: string[];
  setFileNames: (urls: string[]) => void;
  setDocuments: (files: File[]) => void;
  openedModalId: string;
  singleCmmOrderData: NycbsPharmaOrderModel | undefined;
  mergePdfsFromBase64: (
    base64Urls: string[],
    fileName: string,
  ) => Promise<File | null>;
  isMerging: boolean;
  /** Submits the questionnaire; resolves `true` when the PUT succeeded. */
  onSubmitCase: () => Promise<boolean>;
  /** Fired once the submission summary save and questionnaire PUT settle. */
  onSubmitCaseComplete: (
    bothSucceeded: boolean,
    isCaseSubmitted: boolean,
  ) => void;
}

/**
 * Conditionally renders questionnaire-related modals.
 *
 * @param props - Modal data, files, and close/merge handlers
 * @returns Active modal elements for the questionnaire page
 */
const QuestionnaireModals = ({
  onCloseDocumentsModal,
  identifier,
  predictionSummaryIdentifier,
  documents,
  fileNames,
  setFileNames,
  setDocuments,
  openedModalId,
  singleCmmOrderData,
  mergePdfsFromBase64,
  isMerging,
  onSubmitCase,
  onSubmitCaseComplete,
}: QuestionnaireModalsProps) => {
  const dispatch = useDispatch<AppDispatch>();

  /**
   * Clears Redux modal state and closes the oasis-ui modal dialog.
   *
   * @param id - Modal id to close
   */
  const handleCloseSubmissionModal = (id: ModalId) => {
    dispatch(setOpenedModalId({ id: "" }));
    closeModal(id);
  };

  /**
   * Merges selected PDFs then closes the documents modal.
   *
   * @param base64Urls - Base64-encoded PDF payloads to merge
   * @param fileName - Output filename for the merged PDF
   */
  const handleMergePdfs = async (base64Urls: string[], fileName: string) => {
    const mergedFile = await mergePdfsFromBase64(base64Urls, fileName);
    if (!mergedFile) return;
    onCloseDocumentsModal();
  };

  return (
    <>
      {openedModalId === ModalId.DOCUMENT_MERGE_MODAL && (
        <DocumentsModalWithMerge
          onClose={onCloseDocumentsModal}
          documentsName={fileNames}
          documentsList={documents}
          id={identifier ?? ""}
          newDocumentNames={(urls) => setFileNames(urls)}
          newFiles={(files) => setDocuments(files)}
          onMergePdfs={(base64Urls, fileName) =>
            handleMergePdfs(base64Urls, fileName ?? "merged-document.pdf")
          }
          isMerging={isMerging}
        />
      )}
      {openedModalId === ModalId.PREDICTION_SUMMARY_MODAL && (
        <PredictionSummaryModal
          identifier={predictionSummaryIdentifier}
          orderMeta={{
            cmm_key:
              singleCmmOrderData?.[NycbsPharmaOrderKeys.CmmResultKey] ?? "",
            drug: singleCmmOrderData?.[NycbsPharmaOrderKeys.DrugName] ?? "",
            plan: singleCmmOrderData?.[NycbsPharmaOrderKeys.PlanName] ?? "",
            form: singleCmmOrderData?.[NycbsPharmaOrderKeys.FormName] ?? "",
          }}
          onClose={() =>
            handleCloseSubmissionModal(ModalId.PREDICTION_SUMMARY_MODAL)
          }
        />
      )}
      {openedModalId === ModalId.SUBMISSION_SUMMARY_MODAL && (
        <SubmissionSummary
          hideQaSecondStpAndComments
          metaData={{
            data: singleCmmOrderData as unknown as PharmaStpFileModel,
          }}
          submitCaseAction={{
            onSubmitCase,
            onSubmitCaseComplete,
          }}
          onClose={() =>
            handleCloseSubmissionModal(ModalId.SUBMISSION_SUMMARY_MODAL)
          }
        />
      )}
      {openedModalId === ModalId.ASTERA_SUBMISSION_MODAL && (
        <AsterasubmissionModal
          metaData={{
            data: singleCmmOrderData as unknown as PharmaStpFileModel,
          }}
          onClose={() =>
            handleCloseSubmissionModal(ModalId.ASTERA_SUBMISSION_MODAL)
          }
        />
      )}
    </>
  );
};

export default QuestionnaireModals;
